// /mixtapes/:day — one day's Top 100 mixtape: its 100 most liked songs, played back to back from
// #100 (or the last rank there is) to #1, with the day's break between them and the equaliser.
import { useEffect, useState } from "react";
import { dayToDate } from "@eb/shared";
import { api } from "../api";
import { engineHost } from "../audio/host";
import { playMixtape, stopMixtape, useMixtape, type Track } from "../countdown/mixtape";
import { currentRoute, Link, navigate, useTitle } from "../lib/router";
import { useSession } from "../session";
import { Cassette } from "../tapes/Cassette";
import { fetchServerTime } from "../river/player";
import { Visualizer } from "../river/Visualizer";
import { clock } from "../stream";

interface CountdownView {
  day: number;
  date: string;
  available: boolean;
  entries: Track[];
  bookmarked?: boolean;
}

/** Count a day's tape as played once per visit (colours the overview). */
const counted = new Set<number>();

export function MixtapeTape({ day }: { day: number }) {
  useTitle("Top 100 mixtape");
  const [epoch, setEpoch] = useState<number | null>(null);
  const [view, setView] = useState<CountdownView | null>(null);
  const [error, setError] = useState("");
  const m = useMixtape();
  const user = useSession((s) => s.user);

  useEffect(() => {
    void fetchServerTime().then((t) => {
      setEpoch(t.epochMs);
    });
  }, []);

  useEffect(() => {
    if (!Number.isInteger(day) || day < 0) return;
    let alive = true;
    setView(null);
    api<CountdownView>("GET", `/api/mixtapes/${day}`)
      .then((v) => alive && setView(v))
      .catch((e) => alive && setError(String(e)));
    return () => {
      alive = false;
    };
  }, [day]);

  // Play order: the lowest rank first, #1 last.
  const order = view ? [...view.entries].sort((a, b) => b.rank - a.rank) : [];
  const mine = m.day === view?.day && (m.playing || m.starting);
  const current = mine ? order[m.index] : null;
  const start = (index: number) => {
    // Unlock audio inside the click (before any await).
    const host = engineHost();
    if (!view) return;
    void playMixtape(host, view.day, order, index);
    if (!counted.has(view.day)) {
      counted.add(view.day);
      void api("POST", `/api/mixtapes/${view.day}/play`).catch(() => undefined);
    }
  };

  if (error)
    return (
      <main className="page" id="main">
        <p role="alert">{error}</p>
      </main>
    );
  if (epoch === null)
    return (
      <main className="page" id="main">
        <p className="muted">Loading…</p>
      </main>
    );
  const date = dayToDate(day, epoch);
  const label = new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return (
    <main id="main" className="page countdown-page">
      <p>
        <Link href="/mixtapes">← All mixtapes</Link>
      </p>
      <div className="row tape-head">
        <h1 data-testid="countdown-date">Top 100 · {label}</h1>
        {view?.available && (
          <button
            type="button"
            className={`btn btn-small${view.bookmarked ? " btn-primary" : ""}`}
            aria-pressed={!!view.bookmarked}
            onClick={() => {
              if (!user?.nickname) {
                navigate(`/signin?next=${encodeURIComponent(currentRoute())}`);
                return;
              }
              const on = !view.bookmarked;
              api(on ? "PUT" : "DELETE", `/api/mixtapes/${day}/bookmark`)
                .then(() => setView({ ...view, bookmarked: on }))
                .catch(() => undefined);
            }}
            data-testid="tape-bookmark"
          >
            {view.bookmarked ? "★ Bookmarked" : "☆ Bookmark"}
          </button>
        )}
      </div>
      <p className="muted">The day's 100 most liked songs, counting down to #1, played back to back.</p>
      {!view ? (
        <p className="muted">Loading…</p>
      ) : !view.available ? (
        <p className="muted">This day isn't over yet — its countdown opens at midnight (UTC).</p>
      ) : order.length === 0 ? (
        <p className="muted">Nobody liked a song that day.</p>
      ) : (
        <>
          <section className="countdown-display oled" aria-live="polite" data-testid="countdown-display">
            <Cassette
              playing={mine && m.playing}
              progress={mine ? (m.index + m.progress) / Math.max(1, order.length) : 0}
              label={`Top 100 · ${label}`}
            />
            {current ? (
              <>
                <span className="countdown-rank" data-testid="countdown-rank">
                  #{current.rank}
                </span>
                <span className="countdown-song">
                  <strong>{current.title ?? "Untitled song"}</strong> by {current.author ?? "a former contributor"} · ♥{" "}
                  {current.likes}
                </span>
                <Visualizer playing={m.playing} />
                <span className="countdown-progress" aria-hidden="true">
                  <span style={{ width: `${Math.round(m.progress * 100)}%` }} />
                </span>
              </>
            ) : (
              <span className="countdown-song">
                {order.length} songs · #{order[0].rank} → #1 · about{" "}
                {Math.round((order.length * clock.SLOT_MS) / 60_000)} min
              </span>
            )}
          </section>
          <div className="row countdown-controls">
            {mine ? (
              <>
                <button type="button" className="btn" onClick={() => start(Math.max(0, m.index - 1))}>
                  ⏮ Back
                </button>
                <button type="button" className="btn btn-primary" onClick={stopMixtape} data-testid="countdown-stop">
                  ■ Stop
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => (m.index + 1 < order.length ? start(m.index + 1) : stopMixtape())}
                  data-testid="countdown-next"
                >
                  Next ⏭
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => start(0)} data-testid="countdown-play">
                ▶ Play the countdown
              </button>
            )}
            {m.error && (
              <span role="alert" className="error">
                {m.error}
              </span>
            )}
          </div>
          <ol className="countdown-list" data-testid="countdown-list">
            {order.map((t, i) => (
              <li key={t.loopId} className={current?.loopId === t.loopId ? "now" : ""}>
                <button
                  type="button"
                  className="countdown-row"
                  onClick={() => start(i)}
                  aria-label={`Play from #${t.rank}`}
                >
                  <span className="countdown-num">#{t.rank}</span>
                  <span className="countdown-title">{t.title ?? "Untitled song"}</span>
                  <span className="muted">{t.author ?? "former contributor"}</span>
                  <span className="countdown-likes">♥ {t.likes}</span>
                </button>
                <Link className="small" href={`/loop/${encodeURIComponent(t.loopId)}`}>
                  details
                </Link>
              </li>
            ))}
          </ol>
        </>
      )}
    </main>
  );
}
